import { Guid } from '@approvalmax/types';
import { compareHelpers, mathService } from '@approvalmax/utils';
import { backend, domain } from 'modules/data';
import { mapAttachment } from 'modules/data/domain/schemas/requestSchema';

import { tableSortOrderToSortingDirectionMap } from './XeroBillBatchPaymentForm.constants';
import { FilterTrasferData } from './XeroBillBatchPaymentForm.types';

export const createMapFilterTransferWrapper =
    (requestId: Guid) =>
    (filterData: domain.AddBillsToBatchFilterData, sortingConfig: domain.AddBillsToBatchSortingConfig) => {
        return mapFilterTransfer(filterData, sortingConfig, requestId);
    };

export const mapFilterTransfer = (
    filterData: domain.AddBillsToBatchFilterData,
    sortingConfig: domain.AddBillsToBatchSortingConfig,
    xeroBillBatchPaymentRequestId: string
): FilterTrasferData => {
    const { sortBy, sortOrder } = sortingConfig;

    const sortingDirection = tableSortOrderToSortingDirectionMap[sortOrder];

    const xeroContactId = {
        filteringValue: filterData.contactId || null,
        sortingType: sortBy === domain.AddBillsToBatchTableColumns.Supplier ? sortingDirection : null,
    };
    const reference = {
        filteringValue: filterData.reference || null,
    };
    const amountDue = {
        filteringValue: {
            greaterOrEqual: filterData.amountFrom,
            lessOrEqual: filterData.amountTo,
        },
        sortingType: sortBy === domain.AddBillsToBatchTableColumns.AmountDue ? sortingDirection : null,
    };
    const date = {
        filteringValue: {
            type: 0,
            custom: {
                greaterOrEqual: filterData.dateFrom?.toISOString() ?? null,
                lessOrEqual: filterData.dateTo?.toISOString() ?? null,
            },
        },
        sortingType: sortBy === domain.AddBillsToBatchTableColumns.Date ? sortingDirection : null,
    };
    const dueDate = {
        filteringValue: {
            type: 0,
            custom: {
                greaterOrEqual: filterData.dueDateFrom?.toISOString() ?? null,
                lessOrEqual: filterData.dueDateTo?.toISOString() ?? null,
            },
        },
        sortingType: sortBy === domain.AddBillsToBatchTableColumns.DueDate ? sortingDirection : null,
    };
    const plannedDate = {
        filteringValue: {
            type: 0,
            custom: {
                greaterOrEqual: filterData.plannedDateFrom?.toISOString() ?? null,
                lessOrEqual: filterData.plannedDateTo?.toISOString() ?? null,
            },
        },
        sortingType: sortBy === domain.AddBillsToBatchTableColumns.PlannedDate ? sortingDirection : null,
    };

    return {
        xeroBillBatchPaymentRequestId,
        xeroContactId,
        reference,
        amountDue,
        date,
        dueDate,
        plannedDate,
        excludeReserved: filterData.excludeBillsWithZeroAmount,
    };
};

export const mapXeroBillInvoiceRequests = (
    requests: backend.XeroBillInvoiceRequest[],
    integrationCountryCode: backend.OrganisationVersion
): domain.XeroBatchSelectableBillItem[] => {
    return requests.map((request) => ({
        currency: request.currency,
        plannedDate: request.plannedDate,
        xeroBillInvoiceRequestId: request.xeroBillInvoiceRequestId,
        id: request.xeroBillInvoiceRequestId,
        orderNumber: 0,
        friendlyName: request.friendlyName,
        date: request.date,
        dueDate: request.dueDate,
        amountDue: request.amountDue,
        note: request.note,
        bankAccount: request.bankAccountNumber,
        contact: {
            id: request.contact.id,
            text: request.contact.name,
        },
        selected: false,
        details:
            request.details ||
            (integrationCountryCode !== backend.OrganisationVersion.NZ &&
                integrationCountryCode !== backend.OrganisationVersion.AU &&
                (request.reference || request.xeroBillInvoiceReference)) ||
            '',
        amountOnApproval: request.amountOnApproval || 0,
        amountAwaitingPayment: request.amountAwaitingPayment || 0,
        amountInProcessing: request.amountInProcessing || 0,
        amountOnReview: request.amountOnReview || 0,
        amount: mathService.subtract(
            request.amountDue,
            mathService.add(
                mathService.add(request.amountInProcessing || 0, request.amountOnApproval || 0),
                request.amountAwaitingPayment || 0
            )
        ),
        reference:
            request.reference ||
            (integrationCountryCode !== backend.OrganisationVersion.GLOBAL &&
                integrationCountryCode !== backend.OrganisationVersion.UK &&
                integrationCountryCode !== backend.OrganisationVersion.US &&
                request.xeroBillInvoiceReference) ||
            '',
        code: request.code,
        particulars: request.particulars,
        xeroBillInvoiceReference: request.xeroBillInvoiceReference,
        defaultBankAccount: request.bankAccountNumber || null,
        attachments: (request.attachments || [])
            .map(mapAttachment)
            .sort(compareHelpers.comparatorFor<domain.RequestAttachment>(compareHelpers.stringComparator2Asc, 'name')),
        outstandingBalance: request.outstandingBalance,
        overdueBalance: request.overdueBalance,
    }));
};
